/**
 * BillingAgreePage - Billing agreement approval popup
 *
 * This page is opened in a popup by third-party apps via the SDK.
 * The user reviews and approves the billing agreement, and the result
 * is returned to the opener.
 */

import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getApiUrl } from '../api/client'
import { AuthLayout } from '../components/layout/AuthLayout'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { AddPaymentMethod } from '../components/billing/AddPaymentMethod'
import { PaymentIcon, type PaymentType } from 'react-svg-credit-card-payment-icons'
import {
  AppWindow,
  Plus,
  Check,
  AlertTriangle,
  Receipt,
  CalendarClock,
  User,
  Mail,
} from 'lucide-react'

interface AgreementInfo {
  id: string
  app_id: string
  app_name: string | null
  agreement_type: string
  name: string
  description: string | null
  amount_cents: number | null
  billing_interval: string | null
  status: string
  terms: Record<string, unknown> | null
  allowed_payment_methods: string[] | null
  expires_at: string | null
  redirect_uri?: string // Origin for postMessage
}

interface PaymentMethod {
  id: string
  processor: string
  method_type: string
  display_name: string | null
  last_four: string | null
  brand: string | null
  payer_detail: string | null
  payer_name: string | null
  processor_data: Record<string, any> | null
  is_default: boolean
  created_at: string
}

// Get the payment icon type for a payment method
function getPaymentIconType(method: PaymentMethod): PaymentType {
  if (method.processor === 'paypal' || method.method_type === 'paypal') {
    return 'Paypal'
  }

  const brand = method.brand?.toLowerCase() || ''
  if (brand.includes('visa')) return 'Visa'
  if (brand.includes('mastercard') || brand.includes('master')) return 'Mastercard'
  if (brand.includes('amex') || brand.includes('american')) return 'AmericanExpress'
  if (brand.includes('discover')) return 'Discover'
  if (brand.includes('diners')) return 'DinersClub'
  if (brand.includes('jcb')) return 'Jcb'
  if (brand.includes('unionpay')) return 'Unionpay'
  if (brand.includes('maestro')) return 'Maestro'

  return 'Generic'
}

// API_URL is computed dynamically via getApiUrl() for org-scoped subdomains

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100)
}

export function BillingAgreePage() {
  const [searchParams] = useSearchParams()
  const agreementId = searchParams.get('id')

  const [agreement, setAgreement] = useState<AgreementInfo | null>(null)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [approving, setApproving] = useState(false)
  const [showAddMethod, setShowAddMethod] = useState(false)

  // Filter payment methods by what the agreement allows
  const filteredPaymentMethods = paymentMethods.filter((method) => {
    if (!agreement?.allowed_payment_methods) return true // No filter = all allowed
    return agreement.allowed_payment_methods.includes(method.method_type)
  })

  // Determine which add method types are available
  const allowedAddTypes = agreement?.allowed_payment_methods || ['card', 'paypal', 'venmo']

  useEffect(() => {
    if (agreementId) {
      loadAgreement()
      loadPaymentMethods()
    } else {
      setError('No agreement ID provided')
      setLoading(false)
    }
  }, [agreementId])

  async function loadAgreement() {
    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`${getApiUrl()}/api/agreements/${agreementId}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        if (response.status === 404) {
          setError('Agreement not found')
        } else if (response.status === 401) {
          window.location.href = `/login?return_to=${encodeURIComponent(window.location.href)}`
          return
        } else {
          const data = await response.json()
          setError(data.detail || 'Failed to load agreement')
        }
        return
      }

      const data = await response.json()
      setAgreement(data)
    } catch (err) {
      setError('Failed to load agreement')
    } finally {
      setLoading(false)
    }
  }

  async function loadPaymentMethods() {
    try {
      const response = await fetch(`${getApiUrl()}/api/me/payment-methods`, {
        credentials: 'include',
      })

      if (response.ok) {
        const data = await response.json()
        setPaymentMethods(data.payment_methods || [])
        // Auto-select default payment method
        const defaultMethod = data.payment_methods?.find((m: PaymentMethod) => m.is_default)
        if (defaultMethod) {
          setSelectedMethodId(defaultMethod.id)
        } else if (data.payment_methods?.length > 0) {
          setSelectedMethodId(data.payment_methods[0].id)
        }
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err)
    }
  }

  async function handleApprove() {
    if (!agreement) return

    setApproving(true)
    setError(null)

    try {
      const response = await fetch(`${getApiUrl()}/api/agreements/${agreementId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          payment_method_id: selectedMethodId,
        }),
      })

      if (!response.ok) {
        const data = await response.json()
        setError(data.detail || 'Failed to approve agreement')
        return
      }

      // Success - notify opener with validated origin
      if (window.opener) {
        const targetOrigin = getTargetOrigin()
        if (targetOrigin) {
          window.opener.postMessage(
            { type: 'billing_response', success: true, agreement_id: agreement.id },
            targetOrigin
          )
        }
        window.close()
      }
    } catch (err) {
      setError('Failed to approve agreement')
    } finally {
      setApproving(false)
    }
  }

  function getTargetOrigin(): string {
    // Get origin from agreement redirect_uri
    if (agreement?.redirect_uri) {
      try {
        return new URL(agreement.redirect_uri).origin
      } catch {
        // Invalid URL, fall through
      }
    }
    // If no redirect_uri, don't send message to prevent security issues
    return ''
  }

  function handleCancel() {
    if (window.opener) {
      const targetOrigin = getTargetOrigin()
      if (targetOrigin) {
        window.opener.postMessage(
          { type: 'billing_response', success: false, error: 'Cancelled by user' },
          targetOrigin
        )
      }
      window.close()
    }
  }

  // Loading state
  if (loading) {
    return (
      <AuthLayout>
        <Card className="p-8">
          <div className="flex justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
          <p className="mt-4 text-center text-[var(--text-secondary)]">
            Loading billing agreement...
          </p>
        </Card>
      </AuthLayout>
    )
  }

  // Error state (no agreement)
  if (error && !agreement) {
    return (
      <AuthLayout>
        <Card className="p-8">
          <div className="mb-4 flex justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <h2 className="text-center text-lg font-semibold text-[var(--text-primary)]">
            {error}
          </h2>
          <p className="mt-2 text-center text-sm text-[var(--text-secondary)]">
            This billing agreement is no longer valid.
          </p>
          <div className="mt-6">
            <Button variant="outline" className="w-full" onClick={handleCancel}>
              Close
            </Button>
          </div>
        </Card>
      </AuthLayout>
    )
  }

  if (!agreement) {
    return null
  }

  return (
    <AuthLayout>
      <Card className="overflow-hidden">
        {/* App Header */}
        <div className="border-b border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <AppWindow className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="font-semibold text-[var(--text-primary)]">
                {agreement.app_name || 'Application'}
              </h2>
              <p className="text-xs text-[var(--text-secondary)]">
                Requesting payment authorization
              </p>
            </div>
          </div>
        </div>

        {/* Agreement Details */}
        <div className="p-6">
          {/* Agreement Type Badge */}
          <div className="mb-4">
            {agreement.agreement_type === 'one_time' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                <Receipt className="h-3 w-3" />
                One-time purchase
              </span>
            )}
            {agreement.agreement_type === 'subscription' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                <CalendarClock className="h-3 w-3" />
                Subscription
              </span>
            )}
            {agreement.agreement_type === 'usage_based' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                Usage-based billing
              </span>
            )}
          </div>

          {/* Purchase Card */}
          <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-[var(--text-primary)]">
                  {agreement.name}
                </h3>
                {agreement.description && (
                  <p className="mt-1 text-sm text-[var(--text-secondary)]">
                    {agreement.description}
                  </p>
                )}
              </div>
              {agreement.amount_cents !== null && (
                <div className="text-right">
                  <span className="text-xl font-bold text-[var(--text-primary)]">
                    {formatCurrency(agreement.amount_cents)}
                  </span>
                  {agreement.billing_interval && (
                    <span className="text-sm text-[var(--text-secondary)]">
                      /{agreement.billing_interval === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <hr className="my-6 border-[var(--border-subtle)]" />

          {/* Payment Method Selection */}
          <div>
            <h4 className="mb-3 text-sm font-medium text-[var(--text-secondary)]">
              Payment method
            </h4>

            {error && (
              <div className="mb-4 rounded-lg bg-[var(--error-muted)]/30 p-3 text-sm text-[var(--error)]">
                {error}
              </div>
            )}

            <div className="space-y-2">
              {filteredPaymentMethods.map((method) => {
                const isPayPal = method.processor === 'paypal' || method.method_type === 'paypal'
                const isVenmo = method.method_type === 'venmo'
                const processorData = method.processor_data || {}
                const payerName = method.payer_name || processorData.name?.full_name
                const email = method.payer_detail || processorData.email_address

                return (
                  <label
                    key={method.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                      selectedMethodId === method.id
                        ? 'border-primary-500 bg-primary-50 dark:border-primary-500 dark:bg-primary-900/20'
                        : 'border-[var(--border-subtle)] hover:bg-[var(--bg-elevated)]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment-method"
                      value={method.id}
                      checked={selectedMethodId === method.id}
                      onChange={() => setSelectedMethodId(method.id)}
                      className="h-4 w-4 border-[var(--border-default)] text-primary-600 focus:ring-primary-500"
                    />
                    {/* Payment Logo */}
                    <div className="flex h-8 w-12 items-center justify-center">
                      <PaymentIcon type={getPaymentIconType(method)} format="flatRounded" width={40} />
                    </div>
                    {/* Payment Method Details */}
                    <div className="flex-1 min-w-0">
                      {isPayPal || isVenmo ? (
                        <div>
                          <span className="font-medium text-[var(--text-primary)]">
                            {isVenmo ? 'Venmo' : 'PayPal'}
                          </span>
                          {email && (
                            <div className="flex items-center gap-1 text-xs text-[var(--text-secondary)] truncate">
                              <Mail className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">{email}</span>
                            </div>
                          )}
                          {payerName && (
                            <div className="flex items-center gap-1 text-xs text-[var(--text-secondary)]">
                              <User className="h-3 w-3 flex-shrink-0" />
                              <span>{payerName}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className="font-medium text-[var(--text-primary)]">
                            {method.brand ? `${method.brand.charAt(0).toUpperCase()}${method.brand.slice(1)}` : 'Card'} •••• {method.last_four}
                          </span>
                        </div>
                      )}
                    </div>
                    {method.is_default && (
                      <span className="rounded-full bg-[var(--success-muted)]/30 px-2 py-0.5 text-xs font-medium text-[var(--success)]">
                        Default
                      </span>
                    )}
                  </label>
                )
              })}

              {/* Add new payment method */}
              <button
                onClick={() => setShowAddMethod(true)}
                className="flex w-full items-center gap-3 rounded-lg border border-dashed border-[var(--border-default)] p-3 text-[var(--text-secondary)] hover:bg-[var(--bg-elevated)]"
              >
                <Plus className="h-5 w-5" />
                <span>Add new payment method</span>
              </button>
            </div>

            {filteredPaymentMethods.length === 0 && (
              <p className="mt-3 text-sm text-[var(--text-secondary)]">
                {paymentMethods.length > 0
                  ? 'None of your saved payment methods are accepted for this purchase.'
                  : 'You need to add a payment method to continue.'}
              </p>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-[var(--border-subtle)] p-4">
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handleApprove}
              disabled={approving || !selectedMethodId}
            >
              {approving ? (
                'Processing...'
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Approve{' '}
                  {agreement.amount_cents !== null && formatCurrency(agreement.amount_cents)}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Add Payment Method Modal */}
      {showAddMethod && (
        <AddPaymentMethod
          allowedTypes={allowedAddTypes}
          onSuccess={() => {
            setShowAddMethod(false)
            loadPaymentMethods()
          }}
          onCancel={() => setShowAddMethod(false)}
        />
      )}
    </AuthLayout>
  )
}
